import { Link } from "react-router-dom";
import { assetUrl } from "@/utils/assetUrl";

interface CaseItem {
  id: number;
  title: string;
  image?: string;
  client?: string;
  summary?: string;
  description?: string;
  solutionName?: string;
  categoryName?: string;
  metrics?: { value: string; label: string }[];
}

interface Props {
  cases: CaseItem[];
  accentColor: string;
}

export default function DetailCases({ cases, accentColor }: Props) {
  if (cases.length === 0) return null;

  const displayCases = cases.slice(0, 3);

  return (
    <section className="py-16 lg:py-20 bg-[#f9fafb]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 rounded-full" style={{ backgroundColor: accentColor }}></div>
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-0.5">Success Cases</p>
              <h2 className="text-xl font-bold text-[#0d1f3c]">行业标杆案例</h2>
            </div>
          </div>
          <Link
            to="/cases"
            className="flex items-center gap-1.5 text-sm font-medium hover:gap-2.5 transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ color: accentColor }}
          >
            查看全部案例
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </Link>
        </div>

        {/* Cases grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayCases.map((c) => (
            <Link
              key={c.id}
              to={`/cases/${c.id}`}
              className="bg-white rounded-2xl overflow-hidden border border-gray-100 transition-all duration-300 group cursor-pointer block"
            >
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src={assetUrl(c.image)}
                  alt={c.title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/60 to-transparent"></div>
                {(c.solutionName || c.categoryName) && (
                  <div className="absolute top-3 left-3">
                    <span
                      className="px-2.5 py-1 text-white text-xs font-medium rounded-full"
                      style={{ backgroundColor: `${accentColor}dd` }}
                    >
                      {c.solutionName || c.categoryName}
                    </span>
                  </div>
                )}
              </div>
              <div className="p-5">
                {c.client && <p className="text-xs text-gray-400 mb-1.5">{c.client}</p>}
                <h3 className="text-sm font-bold text-[#0d1f3c] mb-2.5 leading-snug line-clamp-2">{c.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-4 line-clamp-2">{c.summary || c.description || ""}</p>
                {c.metrics && c.metrics.length > 0 && (
                  <div className="flex gap-4 pt-3 border-t border-gray-50">
                    {c.metrics.slice(0, 3).map((m) => (
                      <div key={m.label} className="text-center flex-1">
                        <div className="text-sm font-bold" style={{ color: accentColor }}>{m.value}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{m.label}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
